import { auth } from "./firebase";
import { fetchWithTimeout } from "./api";

// The Haven Gmail Service is a SEPARATE Cloud Run service from the main backend.
// It handles the Gmail OAuth connect flow, the Gmail push-notification watch, and
// link scanning. All user endpoints require the same Firebase ID token bearer the
// rest of the portal uses. CORS is open (anyHost) so direct browser calls work.
export const GMAIL_SERVICE_URL =
  process.env.REACT_APP_GMAIL_SERVICE_URL ||
  "https://haven-gmail-service-678457575280.us-central1.run.app";

// Default return hash the service redirects back to after the OAuth round-trip.
export const GMAIL_RETURN_TO = "#gmail";

// Labs feature flag (resolved server-side in /subscription-status `features`) that gates whether
// the "Email Scanning" page is visible. Off by default; enabled per-user/per-org by an operator.
// Must match the key in the backend's LABS_FEATURE_KEYS.
export const GMAIL_LINK_SCANNING_FLAG = "gmailLinkScanning";

// Connection status returned by GET /connection.
export interface GmailConnectionStats {
  linksScanned: number;
  linksFlagged: number;
  flaggedSenders: string[];
}

export interface GmailConnection {
  connected: boolean;
  gmailEmail?: string;
  status?: "active" | "action_needed" | string;
  // Epoch milliseconds — when the Gmail push watch expires and must be renewed.
  watchExpiresAt?: number;
  needsReconnect?: boolean;
  stats?: GmailConnectionStats;
  // Epoch milliseconds — last time the service processed activity for this user.
  lastActivityAt?: number;
}

/** Per-call knobs for the Gmail service calls. */
export interface GmailRequestOptions {
  /**
   * Abort after this many ms. Only account deletion sets it (ENG-3404), where a hung service must
   * become an answer rather than a spinner. Every other call waits as long as `fetch` does: the
   * service scales to zero, and a cold start can outlast a timeout sized for a warm one.
   */
  timeoutMs?: number;
}

/**
 * Fetch against the Gmail service with a Firebase ID token bearer attached.
 * Mirrors `authenticatedFetch` in ./api.ts but targets GMAIL_SERVICE_URL.
 */
export async function gmailAuthenticatedFetch(
  path: string,
  options: RequestInit = {},
  { timeoutMs }: GmailRequestOptions = {},
): Promise<Response> {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error("Not authenticated");
  const token = await currentUser.getIdToken();
  const headers = new Headers(options.headers);
  headers.set("Authorization", `Bearer ${token}`);
  if (options.body && typeof options.body === "string" && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const url = `${GMAIL_SERVICE_URL}${path}`;
  const init = { ...options, headers };
  return timeoutMs === undefined ? fetch(url, init) : fetchWithTimeout(url, init, timeoutMs);
}

/**
 * ENG-3470: this used to throw `"${action} failed (${status})${detail}"`, and `disconnectGmail`'s
 * caller (`DisconnectGmailDialog`) renders `err.message` verbatim — a customer clicking Disconnect
 * could see "Disconnecting Gmail failed (503)." The status code is still worth having, so it goes to
 * `console.warn`; what reaches the dialog is the backend's own reason when it sent one, or a plain
 * sentence naming the action with a nudge to retry.
 */
async function ensureOk(response: Response, action: string): Promise<Response> {
  if (!response.ok) {
    let backendError = "";
    try {
      const data = await response.json();
      backendError = typeof data?.error === "string" ? data.error : "";
    } catch {
      // No JSON body — fall back to the generic message below.
    }
    console.warn(`${action} failed (${response.status})${backendError ? `: ${backendError}` : ""}`);
    throw new Error(backendError || `${action} failed. Try again in a moment.`);
  }
  return response;
}

/**
 * Begin the Gmail connect flow. Returns the Google consent URL the browser should
 * navigate to at top level (never a popup). `returnTo` is the portal hash the
 * service redirects back to after consent (defaults to #gmail).
 */
export async function startGmailConnect(returnTo: string = GMAIL_RETURN_TO): Promise<string> {
  const response = await gmailAuthenticatedFetch("/connect/start", {
    method: "POST",
    body: JSON.stringify({ returnTo }),
  });
  await ensureOk(response, "Starting Gmail connection");
  const data = (await response.json()) as { authorizeUrl?: string };
  if (!data.authorizeUrl) throw new Error("The Gmail service did not return an authorization URL.");
  return data.authorizeUrl;
}

/** Fetch the current Gmail connection status for the signed-in user. */
export async function getGmailConnection(request: GmailRequestOptions = {}): Promise<GmailConnection> {
  const response = await gmailAuthenticatedFetch("/connection", { method: "GET" }, request);
  await ensureOk(response, "Loading Gmail connection");
  return (await response.json()) as GmailConnection;
}

/** Disconnect Gmail. Idempotent on the service side. */
export async function disconnectGmail(request: GmailRequestOptions = {}): Promise<{ disconnected: true; alreadyDisconnected?: boolean }> {
  const response = await gmailAuthenticatedFetch("/connection/disconnect", { method: "POST" }, request);
  await ensureOk(response, "Disconnecting Gmail");
  return (await response.json()) as { disconnected: true; alreadyDisconnected?: boolean };
}
